import { PrismButton } from "@prism-ui/react";
import { APP_TYPE_ID_TO_STRING } from "../constants";

export const RANKS = ["Strong No", "No", "Weak No", "Weak Yes", "Yes", "Strong Yes"];
export const MAX_COMMENT_WORDS = 100;
export const SPREAD_LIMIT = 0.25;

export const countWords = (text) => (text || "").trim().split(/\s+/).filter(Boolean).length;

export const TRACK_META = {
  1: { label: "Mentees", accent: "blue" },
  2: { label: "Mentors", accent: "violet" },
  3: { label: "Committee Members", accent: "teal" },
};
export const TRACK_IDS = [1, 2, 3];
export const singular = (typeId) => APP_TYPE_ID_TO_STRING[typeId] || "Applicant";

export const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};
export const day = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const short = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
// Whole calendar days, so "tomorrow" is 1 however late it is today.
export const daysUntil = (d, now) => Math.round((startOfDay(d) - startOfDay(now)) / 86400000);
const inDays = (n) => (n === 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`);

export const Ring = ({ done, total, size = 120, stroke = 12, accent = "blue", children }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total ? done / total : 1;
  return (
    <div className={`rr-ring rr-accent-${accent}`} style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        {accent === "rainbow" && (
          <defs>
            <linearGradient id="rr-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="hsl(205, 100%, 50%)" />
              <stop offset="55%" stopColor="hsl(260, 70%, 60%)" />
              <stop offset="100%" stopColor="hsl(320, 80%, 60%)" />
            </linearGradient>
          </defs>
        )}
        <circle className="rr-ring-track" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} />
        <circle
          className="rr-ring-fill"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
        />
      </svg>
      <div className="rr-ring-label">{children}</div>
    </div>
  );
};

export const RankChips = ({ value, onPick, disabled }) => (
  <div className="rr-chips" role="radiogroup" aria-label="Rating">
    {RANKS.map((r, i) => (
      <button
        key={r}
        type="button"
        role="radio"
        aria-checked={value === i}
        disabled={disabled}
        className={`rr-chip rr-rank-${i}${value === i ? " lit" : ""}`}
        onClick={() => onPick(i)}
      >
        <kbd>{i + 1}</kbd>
        {r}
      </button>
    ))}
  </div>
);

export const Confetti = () => (
  <div className="rr-confetti" aria-hidden="true">
    {Array.from({ length: 36 }, (_, i) => (
      <span key={i} style={{ "--i": i, "--x": `${(i * 37) % 100}%`, "--d": `${(i % 6) * 0.12}s` }} />
    ))}
  </div>
);

const applicationsClock = (win, now) => {
  const opening = toDate(win?.opening);
  const soft = toDate(win?.soft_deadline);
  const hard = toDate(win?.hard_deadline);
  if (!soft || !hard) return null;
  if (opening && now < opening) {
    return { title: `Applications open ${day(opening)}`, detail: `Public deadline ${day(soft)}` };
  }
  if (now <= soft) {
    return { title: `Applications due ${day(soft)}`, detail: `New applications will keep arriving until then (${inDays(daysUntil(soft, now))})` };
  }
  if (now <= hard) {
    return {
      title: "Applications can still arrive",
      detail: (
        <>
          Public deadline passed {day(soft)} · late applications until <b>{day(hard)}</b> ({inDays(daysUntil(hard, now))})
        </>
      ),
    };
  }
  return { title: "Applications are closed", detail: `The last ones came in by ${day(hard)}` };
};

const ratingClock = (win, now) => {
  const start = toDate(win?.window_start);
  const end = toDate(win?.window_end);
  if (!start || !end) return { title: "Rating dates aren't set yet", detail: "Your chapter officers will open rating." };
  if (now < start) return { title: `Ratings open ${day(start)}`, detail: `${inDays(daysUntil(start, now))} · rating closes ${day(end)}` };
  if (now <= end) {
    return {
      title: `Rating closes ${day(end)}`,
      detail: `${inDays(daysUntil(end, now))} · you can change any rating until then`,
    };
  }
  return { title: `Rating closed ${day(end)}`, detail: "Your ratings are final." };
};

// Marks that sit close together alternate their labels lower so they never overlap.
const STAGGER_PCT = 12;

export const DeadlineStrip = ({ applicationWindow, ratingWindow, now }) => {
  const apps = applicationsClock(applicationWindow, now);
  const rating = ratingClock(ratingWindow, now);
  const marks = [
    ["Applications open", toDate(applicationWindow?.opening), ""],
    ["Public deadline", toDate(applicationWindow?.soft_deadline), ""],
    ["Late cut-off", toDate(applicationWindow?.hard_deadline), ""],
    ["Ratings open", toDate(ratingWindow?.window_start), "rating"],
    ["Rating closes", toDate(ratingWindow?.window_end), "closes"],
  ]
    .filter(([, d]) => d)
    .sort((a, b) => a[1] - b[1]);

  let timeline = null;
  let staggered = false;
  if (marks.length >= 2) {
    const first = marks[0][1];
    const span = marks[marks.length - 1][1] - first || 1;
    const pct = (d) => Math.min(100, Math.max(0, ((d - first) / span) * 100));
    let prev = -Infinity;
    let lowered = false;
    const placed = marks.map(([label, d, kind]) => {
      const at = pct(d);
      const low = !lowered && at - prev < STAGGER_PCT;
      lowered = low;
      prev = at;
      return { label, d, kind, at, low };
    });
    staggered = placed.some((m) => m.low);
    timeline = (
      <div className="rr-timeline" aria-hidden="true">
        <span className="rr-timeline-fill" style={{ width: `${pct(now)}%` }} />
        {placed.map((m) => (
          <span
            key={m.label}
            className={`rr-timeline-mark ${m.kind}${m.low ? " low" : ""}`}
            style={{ left: `${m.at}%` }}
          >
            <i />
            <small>
              {m.label}
              <br />
              {short(m.d)}
            </small>
          </span>
        ))}
        <span className="rr-timeline-today" style={{ left: `${pct(now)}%` }}>
          Today
        </span>
      </div>
    );
  }

  return (
    <section className={`rr-clocks${staggered ? " staggered" : ""}`} aria-label="Deadlines">
      {apps && (
        <div className="rr-clock">
          <span className="rr-clock-icon" aria-hidden="true">📨</span>
          <div>
            <strong>{apps.title}</strong>
            <span>{apps.detail}</span>
          </div>
        </div>
      )}
      <div className="rr-clock">
        <span className="rr-clock-icon" aria-hidden="true">⏳</span>
        <div>
          <strong>{rating.title}</strong>
          <span>{rating.detail}</span>
        </div>
      </div>
      {timeline}
    </section>
  );
};

export const Distribution = ({ dist }) => {
  const total = dist.reduce((a, b) => a + b, 0);
  const heavy = total ? dist.findIndex((n) => n / total > SPREAD_LIMIT) : -1;
  return (
    <section className="rr-dist" aria-label="Your rating spread">
      <h3>Your spread</h3>
      <div className="rr-dist-bar">
        {dist.map((n, i) =>
          n ? <span key={i} className={`rr-rank-${i}`} style={{ flexGrow: n }} title={`${RANKS[i]}: ${n}`} /> : null
        )}
      </div>
      <ul className="rr-dist-legend">
        {dist.map((n, i) => (
          <li key={i}>
            <i className={`rr-rank-${i}`} /> {RANKS[i]} <b>{n}</b>
          </li>
        ))}
      </ul>
      {heavy >= 0 && (
        <p className="rr-hint">
          💡 Lots of <b>{RANKS[heavy]}</b> so far ({Math.round((dist[heavy] / total) * 100)}%). Keep the spread in mind so
          the committee can tell applicants apart.
        </p>
      )}
    </section>
  );
};

export const TrackCard = ({ track, upNext, open, lateUntil, onBegin, onReview }) => {
  const left = track.pending.length;
  const caughtUp = track.total > 0 && left === 0;
  const empty = track.total === 0;
  const meta = TRACK_META[track.typeId];
  return (
    <article
      className={`rr-track rr-accent-${meta.accent}${upNext ? " up-next" : ""}${caughtUp ? " done" : ""}${empty ? " empty" : ""}`}
      aria-label={meta.label}
    >
      {upNext && <span className="rr-ribbon">Up next</span>}
      <Ring done={track.rated.length} total={track.total} size={96} stroke={9} accent={meta.accent}>
        {caughtUp ? (
          <span className="rr-check">✓</span>
        ) : (
          <>
            <b>{left}</b>
            <small>left</small>
          </>
        )}
      </Ring>
      <div className="rr-track-body">
        <h3>{meta.label}</h3>
        <p>
          {empty ? (
            "No applications to read yet."
          ) : caughtUp ? (
            <>
              Caught up · all {track.total} rated.
              {lateUntil && (
                <>
                  <br />
                  <small>New ones may arrive until {day(lateUntil)}.</small>
                </>
              )}
            </>
          ) : (
            `${track.rated.length} of ${track.total} rated`
          )}
        </p>
        <div className="rr-track-actions">
          {left > 0 && open && (
            <PrismButton tone={upNext ? "primary" : "secondary"} display={upNext ? "fill" : "outline"} onPrismClick={onBegin}>
              <span>{track.rated.length === 0 ? "Begin reading" : "Keep reading"}</span>
            </PrismButton>
          )}
          {track.rated.length > 0 && (
            <PrismButton tone="secondary" display={caughtUp ? "outline" : "ghost"} onPrismClick={onReview}>
              <span>{open ? `Review my ${track.rated.length}` : `View my ${track.rated.length}`}</span>
            </PrismButton>
          )}
        </div>
      </div>
    </article>
  );
};
